import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { 
  LogOut, BookOpen, Award, Users, TrendingUp, AlertTriangle,
  CheckCircle, Target, Brain, Heart, Calendar, BarChart3,
  Home, HelpCircle, Link as LinkIcon, Loader2, ChevronRight,
  Smile, Meh, Frown
} from 'lucide-react';
import { format } from 'date-fns';
import ParentSummaryCard from '@/components/ParentSummaryCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MenuItem = ({ icon: Icon, label, active, onClick, badge }) => (
  <button
    onClick={onClick}
    className={`w-full flex items-center justify-between px-4 py-3 text-left transition-colors ${
      active ? 'bg-green-700 text-white' : 'text-green-100 hover:bg-green-700/50'
    }`}
  >
    <div className="flex items-center gap-3">
      <Icon size={18} />
      <span className="font-medium text-sm uppercase tracking-wide">{label}</span>
    </div>
    {badge && <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">{badge}</span>}
  </button>
);

const ParentDashboard = () => {
  const { user, logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('dashboard');
  const [children, setChildren] = useState([]);
  const [selectedChild, setSelectedChild] = useState(null);
  const [childProgress, setChildProgress] = useState(null);
  const [weeklyReport, setWeeklyReport] = useState(null);
  
  // Link child state
  const [showLinkForm, setShowLinkForm] = useState(false);
  const [linkData, setLinkData] = useState({ username: '', code: '' });

  useEffect(() => {
    loadChildren();
  }, []);

  useEffect(() => {
    if (selectedChild) {
      loadChildData(selectedChild.id);
    }
  }, [selectedChild]);

  const loadChildren = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/parent/children`);
      setChildren(response.data.children || []);
      if (response.data.children?.length > 0) {
        setSelectedChild(response.data.children[0]);
      }
    } catch (error) {
      console.error('Error loading children:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadChildData = async (studentId) => {
    try {
      const [progressRes, weeklyRes] = await Promise.all([
        axios.get(`${API}/parent/child/${studentId}/progress`),
        axios.get(`${API}/parent/child/${studentId}/weekly-report`)
      ]);
      setChildProgress(progressRes.data);
      setWeeklyReport(weeklyRes.data);
    } catch (error) {
      console.error('Error loading child data:', error);
    }
  };

  const handleLinkChild = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(
        `${API}/parent/link-child?student_username=${linkData.username}&verification_code=${linkData.code}`
      );
      toast.success(response.data.message);
      setShowLinkForm(false);
      setLinkData({ username: '', code: '' });
      loadChildren();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to link child');
    }
  };

  const getSkillLevelColor = (level) => {
    switch (level) {
      case 'strong': return 'bg-green-100 text-green-700';
      case 'average': return 'bg-yellow-100 text-yellow-700';
      case 'weak': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-500';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-teal-50 flex items-center justify-center">
        <Loader2 className="w-12 h-12 animate-spin text-green-500" />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-gray-100" data-testid="parent-dashboard">
      {/* Sidebar */}
      <aside className="w-64 bg-gradient-to-b from-green-800 to-green-900 text-white flex flex-col shadow-xl">
        <div className="p-4 border-b border-green-700">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center">
              <Users className="text-green-600" size={24} />
            </div>
            <div>
              <h1 className="font-bold text-lg">MyClassAI</h1>
              <p className="text-green-300 text-xs">Parent Portal</p>
            </div>
          </div>
        </div>

        <nav className="flex-1 py-2 overflow-y-auto">
          <MenuItem icon={Home} label="Dashboard" active={activeSection === 'dashboard'} onClick={() => setActiveSection('dashboard')} />
          <MenuItem icon={Brain} label="Skill Progress" active={activeSection === 'skills'} onClick={() => setActiveSection('skills')} />
          <MenuItem icon={BarChart3} label="Weekly Report" active={activeSection === 'weekly'} onClick={() => setActiveSection('weekly')} />
          <MenuItem icon={Heart} label="Well-being" active={activeSection === 'wellbeing'} onClick={() => setActiveSection('wellbeing')} />
          <MenuItem icon={LinkIcon} label="Link Child" active={activeSection === 'link'} onClick={() => setActiveSection('link')} />
          <MenuItem icon={HelpCircle} label="Support" active={activeSection === 'support'} onClick={() => setActiveSection('support')} />
        </nav>

        <div className="p-4 border-t border-green-700">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-green-600 rounded-full flex items-center justify-center">
              <span className="text-white font-bold">{user?.name?.charAt(0) || 'P'}</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-sm truncate">{user?.name || 'Parent'}</p>
              <p className="text-green-300 text-xs">Parent Account</p>
            </div>
          </div>
          <Button variant="ghost" className="w-full justify-start text-green-200 hover:text-white hover:bg-green-700" onClick={logout}>
            <LogOut size={18} className="mr-2" /> Logout
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        <header className="bg-white shadow-sm px-6 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold text-gray-800">
                {activeSection === 'dashboard' && 'Dashboard'}
                {activeSection === 'skills' && 'Skill Progress'}
                {activeSection === 'weekly' && 'Weekly Report'}
                {activeSection === 'wellbeing' && 'Well-being'}
                {activeSection === 'link' && 'Link Child'}
                {activeSection === 'support' && 'Support'}
              </h2>
              <p className="text-gray-500 text-sm">Welcome, {user?.name || 'Parent'}!</p>
            </div>
            
            {/* Child Selector */}
            {children.length > 0 && (
              <select
                value={selectedChild?.id || ''}
                onChange={(e) => {
                  const child = children.find(c => c.id === e.target.value);
                  setSelectedChild(child);
                }}
                className="border rounded-lg px-4 py-2 focus:ring-2 focus:ring-green-500"
              >
                {children.map(child => (
                  <option key={child.id} value={child.id}>
                    {child.name} - Class {child.class_level}
                  </option>
                ))}
              </select>
            )}
          </div>
        </header>

        <div className="p-6">
          {/* No Children Linked */}
          {children.length === 0 && activeSection !== 'link' && (
            <div className="bg-white rounded-xl shadow-sm p-12 text-center">
              <Users className="mx-auto text-gray-300 mb-4" size={64} />
              <h3 className="text-xl font-semibold text-gray-600 mb-2">No Children Linked</h3>
              <p className="text-gray-400 mb-6">Link your child's account to view their progress</p>
              <Button onClick={() => setActiveSection('link')} className="bg-green-600 hover:bg-green-700">
                <LinkIcon className="mr-2" size={18} /> Link Child Account
              </Button>
            </div>
          )}

          {/* Dashboard View */}
          {activeSection === 'dashboard' && selectedChild && childProgress && (
            <div className="space-y-6">
              {/* Child Info Card */}
              <div className="bg-gradient-to-r from-green-500 to-teal-500 rounded-2xl p-6 text-white">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center text-2xl font-bold">
                    {selectedChild.name.charAt(0)}
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold">{selectedChild.name}</h2>
                    <p className="opacity-80">Class {selectedChild.class_level} • Roll No: {selectedChild.roll_number}</p>
                  </div>
                </div>
              </div>

              {/* Parent Summary — improvement %, tests, homework, class */}
              <ParentSummaryCard studentId={selectedChild.id} />

              {/* Skill Summary */}
              <div className="grid grid-cols-4 gap-4">
                <div className="bg-white rounded-xl p-5 shadow-sm">
                  <Brain className="text-purple-500 mb-2" size={32} />
                  <p className="text-3xl font-bold text-gray-800">{childProgress.skill_summary.total_assessed}</p>
                  <p className="text-sm text-gray-500">Skills Assessed</p>
                </div>
                <div className="bg-green-50 rounded-xl p-5 shadow-sm border border-green-200">
                  <CheckCircle className="text-green-500 mb-2" size={32} />
                  <p className="text-3xl font-bold text-green-700">{childProgress.skill_summary.strong}</p>
                  <p className="text-sm text-green-600">Strong Skills</p>
                </div>
                <div className="bg-yellow-50 rounded-xl p-5 shadow-sm border border-yellow-200">
                  <Target className="text-yellow-500 mb-2" size={32} />
                  <p className="text-3xl font-bold text-yellow-700">{childProgress.skill_summary.average}</p>
                  <p className="text-sm text-yellow-600">Progressing</p>
                </div>
                <div className="bg-red-50 rounded-xl p-5 shadow-sm border border-red-200">
                  <AlertTriangle className="text-red-500 mb-2" size={32} />
                  <p className="text-3xl font-bold text-red-700">{childProgress.skill_summary.weak}</p>
                  <p className="text-sm text-red-600">Needs Focus</p>
                </div>
              </div>

              {/* Focus Areas */}
              {childProgress.focus_areas.length > 0 && (
                <div className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-red-400">
                  <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
                    <AlertTriangle className="text-red-500" size={20} />
                    Areas Needing Focus
                  </h3>
                  <div className="space-y-2">
                    {childProgress.focus_areas.map((area, i) => (
                      <div key={i} className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                        <div>
                          <p className="font-medium">{area.skill_name}</p>
                          <p className="text-sm text-gray-500">{area.chapter}</p>
                        </div>
                        <Badge className="bg-red-100 text-red-700">{area.accuracy}%</Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Strengths */}
              {childProgress.strengths.length > 0 && (
                <div className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-green-400">
                  <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
                    <CheckCircle className="text-green-500" size={20} />
                    Strong Areas
                  </h3>
                  <div className="space-y-2">
                    {childProgress.strengths.map((area, i) => (
                      <div key={i} className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                        <div>
                          <p className="font-medium">{area.skill_name}</p>
                          <p className="text-sm text-gray-500">{area.chapter}</p>
                        </div>
                        <Badge className="bg-green-100 text-green-700">{area.accuracy}%</Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Recommendations */}
              <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-6">
                <h3 className="font-bold text-gray-800 mb-4">💡 Recommendations for Parents</h3>
                <ul className="space-y-2">
                  {childProgress.recommendations.filter(r => r).map((rec, i) => (
                    <li key={i} className="flex items-start gap-2 text-gray-700">
                      <ChevronRight className="text-blue-500 mt-0.5" size={16} />
                      {rec}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {/* Skills View */}
          {activeSection === 'skills' && selectedChild && childProgress && (
            <div className="space-y-6">
              <div className="bg-white rounded-xl shadow-sm p-6">
                <h3 className="font-bold text-gray-800 mb-4">Skill Mastery Overview</h3>
                
                {/* Improvement Trend */}
                <div className="grid grid-cols-3 gap-4 mb-6">
                  <div className="p-4 bg-green-50 rounded-xl text-center">
                    <TrendingUp className="mx-auto text-green-500 mb-2" size={24} />
                    <p className="text-2xl font-bold text-green-700">{childProgress.improvement_trend.improving}</p>
                    <p className="text-sm text-green-600">Improving</p>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-xl text-center">
                    <Target className="mx-auto text-gray-500 mb-2" size={24} />
                    <p className="text-2xl font-bold text-gray-700">{childProgress.improvement_trend.stable}</p>
                    <p className="text-sm text-gray-600">Stable</p>
                  </div>
                  <div className="p-4 bg-red-50 rounded-xl text-center">
                    <AlertTriangle className="mx-auto text-red-500 mb-2" size={24} />
                    <p className="text-2xl font-bold text-red-700">{childProgress.improvement_trend.declining}</p>
                    <p className="text-sm text-red-600">Needs Attention</p>
                  </div>
                </div>

                {/* All Focus Areas */}
                <h4 className="font-semibold text-gray-700 mb-3">All Focus Areas</h4>
                {childProgress.focus_areas.length > 0 ? (
                  <div className="space-y-2">
                    {childProgress.focus_areas.map((area, i) => (
                      <div key={i} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <span className="font-medium">{area.skill_name}</span>
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-gray-500">{area.chapter}</span>
                          <Badge className={area.accuracy >= 50 ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'}>
                            {area.accuracy}%
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-4">No weak areas - great job!</p>
                )}
              </div>
            </div>
          )}

          {/* Weekly Report View */}
          {activeSection === 'weekly' && selectedChild && weeklyReport && (
            <div className="space-y-6">
              <div className="bg-white rounded-xl shadow-sm p-6">
                <h3 className="font-bold text-gray-800 mb-4">
                  Weekly Report for {weeklyReport.student_name}
                </h3>
                
                <div className="grid grid-cols-3 gap-4 mb-6">
                  <div className="p-4 bg-purple-50 rounded-xl text-center">
                    <Brain className="mx-auto text-purple-500 mb-2" size={24} />
                    <p className="text-2xl font-bold text-purple-700">{weeklyReport.activity_summary.diagnostic_tests_taken}</p>
                    <p className="text-sm text-purple-600">Skill Builder</p>
                  </div>
                  <div className="p-4 bg-blue-50 rounded-xl text-center">
                    <BookOpen className="mx-auto text-blue-500 mb-2" size={24} />
                    <p className="text-2xl font-bold text-blue-700">{weeklyReport.activity_summary.mock_tests_taken}</p>
                    <p className="text-sm text-blue-600">Mock Tests</p>
                  </div>
                  <div className="p-4 bg-pink-50 rounded-xl text-center">
                    <Heart className="mx-auto text-pink-500 mb-2" size={24} />
                    <p className="text-2xl font-bold text-pink-700">{weeklyReport.activity_summary.mental_health_checkins}</p>
                    <p className="text-sm text-pink-600">Well-being Check-ins</p>
                  </div>
                </div>

                {/* Test Performance */}
                <div className="bg-gray-50 rounded-xl p-4 mb-6">
                  <h4 className="font-semibold text-gray-700 mb-3">Test Performance</h4>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-gray-800">
                        {Math.round(weeklyReport.test_performance.average_score)}%
                      </p>
                      <p className="text-sm text-gray-500">Average Score</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-green-600">{weeklyReport.test_performance.tests_passed}</p>
                      <p className="text-sm text-gray-500">Passed</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-red-600">{weeklyReport.test_performance.tests_need_work}</p>
                      <p className="text-sm text-gray-500">Needs Work</p>
                    </div>
                  </div>
                </div>

                {/* Parent Tips */}
                <div className="bg-gradient-to-r from-green-50 to-teal-50 rounded-xl p-4">
                  <h4 className="font-semibold text-gray-700 mb-3">💡 Tips for You</h4>
                  <ul className="space-y-2">
                    {weeklyReport.parent_tips.map((tip, i) => (
                      <li key={i} className="flex items-start gap-2 text-gray-600">
                        <ChevronRight className="text-green-500 mt-0.5" size={16} />
                        {tip}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* Link Child View */}
          {activeSection === 'link' && (
            <div className="max-w-md mx-auto">
              <div className="bg-white rounded-xl shadow-sm p-6">
                <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
                  <LinkIcon className="text-green-500" size={20} />
                  Link Child Account
                </h3>
                
                <p className="text-gray-600 mb-6">
                  Enter your child's username and their roll number as verification code.
                </p>

                <form onSubmit={handleLinkChild} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Child's Username
                    </label>
                    <Input
                      value={linkData.username}
                      onChange={(e) => setLinkData({ ...linkData, username: e.target.value })}
                      placeholder="e.g., student1"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Verification Code (Roll Number)
                    </label>
                    <Input
                      value={linkData.code}
                      onChange={(e) => setLinkData({ ...linkData, code: e.target.value })}
                      placeholder="e.g., 001"
                      required
                    />
                  </div>
                  <Button type="submit" className="w-full bg-green-600 hover:bg-green-700">
                    Link Child Account
                  </Button>
                </form>

                {/* Linked Children */}
                {children.length > 0 && (
                  <div className="mt-8">
                    <h4 className="font-semibold text-gray-700 mb-3">Linked Children</h4>
                    <div className="space-y-2">
                      {children.map(child => (
                        <div key={child.id} className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                          <div>
                            <p className="font-medium">{child.name}</p>
                            <p className="text-sm text-gray-500">Class {child.class_level}</p>
                          </div>
                          <CheckCircle className="text-green-500" size={20} />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Support View */}
          {activeSection === 'support' && (
            <div className="bg-white rounded-xl shadow-sm p-12 text-center">
              <HelpCircle className="mx-auto text-gray-300 mb-4" size={64} />
              <h3 className="text-xl font-semibold text-gray-600">Need Help?</h3>
              <p className="text-gray-400 mt-2">Contact your child's school for support</p>
              <p className="text-gray-500 mt-4">Email: support@myclassai.net</p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default ParentDashboard;
